import React, { useEffect, useMemo, useState } from 'react'
import {
  Box, Flex, Text, Spinner, IconButton, useToast, Button, Center, Tooltip,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter,
  SimpleGrid,
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { LuEye, LuMail, LuFileText, LuInbox, LuLayoutTemplate, LuPencil, LuTrash2, LuPlus } from 'react-icons/lu'
import Pagination from 'react-js-pagination'
import moment from 'moment'
import Header from '../../../components/Header'
import BoxWrapper from '../../../components/BoxWrapper/BoxWrapper'
import { fetchWrapper } from '../../../helpers'
import { decodeHtmlEntities } from '../../TemplateManager/CreateTemplate/EmailDesigner/htmlExport'
import { PRIMARY_BG } from '../../../theme/brand';

const ACCENT = PRIMARY_BG
const TEMPLATE_COLOR = '#7c3aed'
const EMAIL_COLOR = '#0d9488'
const ITEMS_PER_PAGE = 12
const THUMB_H = 176
const THUMB_SCALE = 0.25

function DeleteConfirmModal({ isOpen, onClose, onConfirm, name, isDeleting }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
      <ModalOverlay />
      <ModalContent borderRadius="16px">
        <ModalHeader fontSize="15px">Delete Email Template</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="13px" color="gray.600">
            Are you sure you want to delete <b>{name}</b>? This action cannot be undone.
          </Text>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button size="sm" colorScheme="red" isLoading={isDeleting} onClick={onConfirm}>Delete</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

function PreviewModal({ isOpen, onClose, html, name }) {
  const hasContent = Boolean(html && html.trim())
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent h="88vh" display="flex" flexDir="column" borderRadius="16px" overflow="hidden">
        <ModalHeader fontSize="13px" fontWeight="700" py={3} px={5} bg="gray.50" borderBottom="1px solid" borderBottomColor="gray.100">
          {name}
        </ModalHeader>
        <ModalCloseButton top={2.5} right={4} />
        <ModalBody p={0} flex={1} overflow="hidden" bg="gray.50">
          {hasContent ? (
            <iframe
              key={html.length}
              srcDoc={html}
              title="Email Preview"
              style={{ display: 'block', width: '100%', height: '100%', border: 'none' }}
            />
          ) : (
            <Center h="100%">
              <Text fontSize="13px" color="gray.400">No preview available.</Text>
            </Center>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

function EmailTile({ item, accentColor, previewLoading, onPreview, onEdit, onDelete }) {
  // The list endpoint doesn't return the full html_code — fetch it per-tile so thumbnails can render
  const [thumbHtml, setThumbHtml] = useState(item.html_code || '')
  const [thumbLoading, setThumbLoading] = useState(!item.html_code)

  useEffect(() => {
    if (item.html_code) return
    let cancelled = false
    async function loadThumb() {
      try {
        const url = `${process.env.REACT_APP_API_URL}/email-template/get-by-id?id=${item.id}`
        const res = await fetchWrapper.get(url)
        if (!cancelled) setThumbHtml(res?.data?.html_code || '')
      } catch {
        // fall back to placeholder icon below
      } finally {
        if (!cancelled) setThumbLoading(false)
      }
    }
    loadThumb()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id])

  const hasThumb = Boolean(thumbHtml && thumbHtml.trim())
  const decodedHtml = hasThumb ? decodeHtmlEntities(thumbHtml) : ''

  return (
    <Box
      role="group"
      borderRadius="16px"
      bg="white"
      overflow="hidden"
      boxShadow="0 1px 2px rgba(16,24,40,0.06), 0 1px 3px rgba(16,24,40,0.08)"
      transition="all 0.2s ease"
      _hover={{ boxShadow: '0 16px 32px rgba(16,24,40,0.14)', transform: 'translateY(-4px)' }}
    >
      <Box position="relative" h={`${THUMB_H}px`} bg="gray.50" overflow="hidden">
        {thumbLoading ? (
          <Center h="100%">
            <Spinner size="sm" color="gray.300" />
          </Center>
        ) : hasThumb ? (
          <div
            style={{
              width: `${100 / THUMB_SCALE}%`,
              height: `${THUMB_H / THUMB_SCALE}px`,
              transformOrigin: 'top left',
              transform: `scale(${THUMB_SCALE})`,
            }}
          >
            <iframe
              srcDoc={decodedHtml}
              title={item.name}
              tabIndex={-1}
              style={{ width: '100%', height: '100%', border: 'none', display: 'block', pointerEvents: 'none' }}
            />
          </div>
        ) : (
          <Center h="100%">
            <LuFileText size={24} color="#c7cad1" />
          </Center>
        )}

        {/* Top accent line — echoes the tab color */}
        <Box position="absolute" top={0} left={0} right={0} h="3px" bg={accentColor} />

        {/* Bottom fade so clipped/scrolling content reads as intentional */}
        <Box position="absolute" bottom={0} left={0} right={0} h="36px" pointerEvents="none"
          style={{ background: 'linear-gradient(transparent, rgba(255,255,255,0.95))' }} />

        {/* Hover action overlay */}
        <Flex
          position="absolute" inset={0}
          opacity={0} _groupHover={{ opacity: 1 }} transition="opacity 0.18s ease"
          bg="blackAlpha.500" backdropFilter="blur(1px)"
          align="center" justify="center" gap={2.5}
        >
          <Tooltip label="Preview" hasArrow fontSize="xs">
            <IconButton
              aria-label="Preview" icon={previewLoading ? <Spinner size="xs" /> : <LuEye size={15} />}
              size="sm" isRound bg="white" isDisabled={previewLoading}
              shadow="md" _hover={{ bg: 'gray.50', transform: 'scale(1.08)' }} transition="transform 0.12s"
              onClick={onPreview}
            />
          </Tooltip>
          <Tooltip label="Edit" hasArrow fontSize="xs">
            <IconButton
              aria-label="Edit" icon={<LuPencil size={14} />}
              size="sm" isRound bg="white" shadow="md"
              _hover={{ bg: 'gray.50', transform: 'scale(1.08)' }} transition="transform 0.12s"
              onClick={onEdit}
            />
          </Tooltip>
          <Tooltip label="Delete" hasArrow fontSize="xs">
            <IconButton
              aria-label="Delete" icon={<LuTrash2 size={14} />}
              size="sm" isRound bg="white" color="red.500" shadow="md"
              _hover={{ bg: 'red.50', transform: 'scale(1.08)' }} transition="transform 0.12s"
              onClick={onDelete}
            />
          </Tooltip>
        </Flex>
      </Box>

      <Box p={3.5}>
        <Text fontSize="13px" fontWeight="700" color="gray.800" noOfLines={1} mb={1}>{item.name}</Text>
        <Flex justify="space-between" align="center">
          <Text fontSize="10.5px" color="gray.400" fontWeight="500">
            {Array.isArray(item.variables) ? item.variables.length : 0} variable{Array.isArray(item.variables) && item.variables.length === 1 ? '' : 's'}
          </Text>
          <Text fontSize="10px" color="gray.400">
            {item.updatedAt || item.savedAt ? moment(item.updatedAt || item.savedAt).format('DD MMM YY') : ''}
          </Text>
        </Flex>
      </Box>
    </Box>
  )
}

function TabListing({ items, isTemplate, accentColor, previewLoadingId, onPreview, onEdit, onDeleteRequest }) {
  const [activePage, setActivePage] = useState(1)

  // Reset to page 1 whenever the underlying item set changes size (e.g. after a delete)
  useEffect(() => { setActivePage(1) }, [items.length])

  const pageItems = items.slice((activePage - 1) * ITEMS_PER_PAGE, activePage * ITEMS_PER_PAGE)

  if (items.length === 0) {
    const EmptyIcon = isTemplate ? LuLayoutTemplate : LuInbox
    return (
      <Center py={20} flexDir="column" gap={3}>
        <Box w="52px" h="52px" borderRadius="full" bg="gray.50" display="flex" alignItems="center" justifyContent="center">
          <EmptyIcon size={22} color="#c7cad1" />
        </Box>
        <Text fontSize="13px" color="gray.400">
          No {isTemplate ? 'templates' : 'emails'} yet. Click "Create New" to get started.
        </Text>
      </Center>
    )
  }

  return (
    <>
      <SimpleGrid columns={{ base: 2, md: 3, xl: 4 }} spacing={5}>
        {pageItems.map(item => (
          <EmailTile
            key={item.id}
            item={item}
            accentColor={accentColor}
            previewLoading={previewLoadingId === item.id}
            onPreview={() => onPreview(item)}
            onEdit={() => onEdit(item)}
            onDelete={() => onDeleteRequest(item)}
          />
        ))}
      </SimpleGrid>

      {items.length > ITEMS_PER_PAGE && (
        <Flex justify="center" mt={6}>
          <Pagination
            activePage={activePage}
            itemsCountPerPage={ITEMS_PER_PAGE}
            totalItemsCount={items.length}
            pageRangeDisplayed={5}
            onChange={setActivePage}
            itemClass="page-item"
            linkClass="page-link"
          />
        </Flex>
      )}
    </>
  )
}

function SegmentedTabs({ tabs, active, onChange }) {
  return (
    <Flex
      bg="gray.100" borderRadius="12px" p="4px" gap="2px"
      display="inline-flex" mb={6}
    >
      {tabs.map((tab, i) => (
        <Flex
          key={tab.label}
          as="button"
          onClick={() => onChange(i)}
          align="center" gap={2}
          px={4} py={2} borderRadius="9px"
          cursor="pointer"
          bg={active === i ? 'white' : 'transparent'}
          shadow={active === i ? '0 1px 3px rgba(0,0,0,0.12)' : 'none'}
          transition="all 0.15s ease"
          _hover={active === i ? {} : { bg: 'whiteAlpha.600' }}
        >
          <Box w="7px" h="7px" borderRadius="full" bg={tab.color} flexShrink={0} />
          <Text fontSize="13px" fontWeight="700" color={active === i ? 'gray.800' : 'gray.500'}>
            {tab.label}
          </Text>
          <Box
            fontSize="10.5px" fontWeight="700" px={1.5} py={0.5} borderRadius="6px"
            bg={active === i ? `${tab.color}15` : 'transparent'}
            color={active === i ? tab.color : 'gray.400'}
          >
            {tab.count}
          </Box>
        </Flex>
      ))}
    </Flex>
  )
}

function EmailTemplateList() {
  const navigate = useNavigate()
  const toast = useToast()

  const [activeTab, setActiveTab] = useState(0) // 0 = Emails, 1 = Templates
  const [allItems, setAllItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [previewItem, setPreviewItem] = useState(null)
  const [previewLoadingId, setPreviewLoadingId] = useState(null)

  useEffect(() => {
    fetchAll()
  }, [])

  // The backend's is_template filter/pagination.total isn't reliable, so fetch the
  // full list once and split/count/paginate the two tabs entirely client-side.
  async function fetchAll() {
    setLoading(true)
    try {
      const url = `${process.env.REACT_APP_API_URL}/email-template/list?page=1&limit=500`
      const res = await fetchWrapper.get(url)
      setAllItems(res?.data || [])
    } catch (error) {
      toast({ title: 'Failed to load email templates', status: 'error', duration: 3000 })
    } finally {
      setLoading(false)
    }
  }

  const emails = useMemo(() => allItems.filter(i => !i.is_template), [allItems])
  const templates = useMemo(() => allItems.filter(i => i.is_template), [allItems])

  async function handleDeleteConfirm() {
    if (!deleteTarget) return
    setIsDeleting(true)
    try {
      const url = `${process.env.REACT_APP_API_URL}/email-template/delete`
      await fetchWrapper.post(url, { id: deleteTarget.id })
      toast({ title: 'Deleted', status: 'success', duration: 2000 })
      setDeleteTarget(null)
      fetchAll()
    } catch (error) {
      toast({ title: 'Failed to delete', status: 'error', duration: 3000 })
    } finally {
      setIsDeleting(false)
    }
  }

  // The list endpoint may omit the full html_code for payload size — fetch the
  // full record on demand so preview always has the latest generated markup.
  async function handlePreview(item) {
    setPreviewLoadingId(item.id)
    try {
      const url = `${process.env.REACT_APP_API_URL}/email-template/get-by-id?id=${item.id}`
      const res = await fetchWrapper.get(url)
      setPreviewItem(res?.data || item)
    } catch (error) {
      toast({ title: 'Failed to load preview', status: 'error', duration: 3000 })
    } finally {
      setPreviewLoadingId(null)
    }
  }

  const handleEdit = (item) => navigate(`/tools/email-designer/${item.id}`)

  return (
    <>
      <Header />
      <BoxWrapper>
        <Box p={{ base: 4, md: 6 }} bg="white" borderRadius="18px" boxShadow="0 2px 10px rgba(16,24,40,0.06), 0 1px 3px rgba(16,24,40,0.04)">
          <Flex justify="space-between" align="center" mb={6} wrap="wrap" gap={3}>
            <Flex align="center" gap={3}>
              <Box
                w={10} h={10} borderRadius="12px"
                style={{ background: `linear-gradient(135deg, ${ACCENT} 0%, var(--brand-primary-hover) 100%)` }}
                display="flex" alignItems="center" justifyContent="center"
                shadow={`0 4px 12px ${ACCENT}45`}
              >
                <LuMail size={17} color="white" />
              </Box>
              <Box>
                <Text fontSize="19px" fontWeight="500" color="gray.800" letterSpacing="-0.01em">Email Templates</Text>
                <Text fontSize="12.5px" color="gray.500">Manage your saved emails and reusable templates</Text>
              </Box>
            </Flex>
            <Button
              leftIcon={<LuPlus size={14} />}
              size="sm"
              bg={ACCENT}
              color="white"
              borderRadius="9px"
              fontWeight="600"
              px={4}
              shadow={`0 2px 8px ${ACCENT}35`}
              _hover={{ bg: 'var(--brand-primary-hover)' }}
              onClick={() => navigate('/tools/email-designer')}
            >
              Create New
            </Button>
          </Flex>

          <SegmentedTabs
            active={activeTab}
            onChange={setActiveTab}
            tabs={[
              { label: 'Emails', color: EMAIL_COLOR, count: emails.length },
              { label: 'Templates', color: TEMPLATE_COLOR, count: templates.length },
            ]}
          />

          {loading ? (
            <Center py={20}><Spinner size="md" color={ACCENT} thickness="3px" /></Center>
          ) : (
            <>
              <Box display={activeTab === 0 ? 'block' : 'none'}>
                <TabListing
                  items={emails}
                  isTemplate={false}
                  accentColor={EMAIL_COLOR}
                  previewLoadingId={previewLoadingId}
                  onPreview={handlePreview}
                  onEdit={handleEdit}
                  onDeleteRequest={setDeleteTarget}
                />
              </Box>
              <Box display={activeTab === 1 ? 'block' : 'none'}>
                <TabListing
                  items={templates}
                  isTemplate={true}
                  accentColor={TEMPLATE_COLOR}
                  previewLoadingId={previewLoadingId}
                  onPreview={handlePreview}
                  onEdit={handleEdit}
                  onDeleteRequest={setDeleteTarget}
                />
              </Box>
            </>
          )}
        </Box>

        <DeleteConfirmModal
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          name={deleteTarget?.name}
          isDeleting={isDeleting}
        />

        <PreviewModal
          isOpen={!!previewItem}
          onClose={() => setPreviewItem(null)}
          html={decodeHtmlEntities(previewItem?.html_code)}
          name={previewItem?.name}
        />
      </BoxWrapper>
    </>
  )
}

export default EmailTemplateList
